import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import AnalyticsCard from '../AnalyticsCard';
import { Loader } from 'ncoded-component-library';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import { ResponsivePie } from '@nivo/pie';
import {
  COLORS,
  DARK_COLORS,
  LABEL_COLOR_DARK,
  LABEL_COLOR_LIGHT,
  MARGINS,
  NUM_OF_COLORS,
  THEME,
} from '../DealChartCard/data';
import { formatPercentage } from 'utils';
import api from 'api';
import EnterpriseContext from '../../../../providers/Enterprise/Enterprise.context';
import useQueryParams from 'hooks/useQueryParams';
import DealsFilter from './components/DealsFilter';
import { FIRST_PAGE, MAX_LIMIT } from 'data/global';
import { DealRoom } from 'models/DealRoom';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

import './ActiveLeadsCard.styles.scss';
import './ActiveLeadsCard.styles.responsive.scss';

const LEAD_ACTIIVTY_FILTER_KEY = 'leadActivityDealRoomId';

type ActiveLeadsCardProps = {
  className?: string;
};

const ActiveLeadsCard: React.FC<ActiveLeadsCardProps> = (props) => {
  const { className } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [leadsActivityStats, setLeadsActivityStats] = useState([]);
  const [deals, setDeals] = useState<DealRoom[]>([]);

  const { enterprise } = useContext(EnterpriseContext);

  const { params } = useQueryParams();

  const leadActivityDealRoomId = params[LEAD_ACTIIVTY_FILTER_KEY];

  const leadsActivityStatsLength = leadsActivityStats?.length;

  const showEmptyState = leadsActivityStats.every(({ count }) => !count);

  const classes = classNames('active-leads-card', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const filterOptions = useMemo(
    () => deals.map(({ _id, name }) => ({ label: name, value: _id })),
    [deals],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const {
          data: { items },
        } = await api.enterpriseAnalytics.getAllDeals(enterprise?._id, {
          $page: FIRST_PAGE,
          $limit: MAX_LIMIT,
        });

        setDeals(items);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [enterprise?._id, getMixpanelProps]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data } = await api.enterpriseAnalytics.getLeadActivity(
          enterprise?._id,
          leadActivityDealRoomId || undefined,
        );

        setLeadsActivityStats(
          data.map(({ status, percentage, count }) => ({
            id: status,
            label: t(status),
            count,
            value: percentage,
          })),
        );
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [enterprise?._id, getMixpanelProps, leadActivityDealRoomId, t]);

  return (
    <AnalyticsCard
      className={classes}
      title={t('leadActivity')}
      action={
        <DealsFilter
          filterKey={LEAD_ACTIIVTY_FILTER_KEY}
          options={filterOptions}
        />
      }
    >
      {isLoading ? (
        <Loader />
      ) : showEmptyState ? (
        <ReportsEmptyState />
      ) : (
        <>
          <div className="active-leads-card__graph">
            <div className="chart-wrapper">
              <ResponsivePie
                data={leadsActivityStats}
                enableArcLinkLabels={false}
                isInteractive={false}
                colors={COLORS}
                margin={MARGINS}
                theme={THEME}
                arcLabelsRadiusOffset={leadsActivityStatsLength === 1 ? 0 : 0.5}
                valueFormat={formatPercentage}
                arcLabelsTextColor={({ color }) =>
                  DARK_COLORS.includes(color)
                    ? LABEL_COLOR_LIGHT
                    : LABEL_COLOR_DARK
                }
              />
            </div>

            <div className="chart-leggend">
              {leadsActivityStats.map(({ label, count }, index) => (
                <div key={label} className="chart-leggend__item">
                  <span
                    style={{ backgroundColor: COLORS[index % NUM_OF_COLORS] }}
                  />
                  <div>
                    {label}
                    <p>{count}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="description">{t('activeLeadsDescription')}</p>
        </>
      )}
    </AnalyticsCard>
  );
};

export default ActiveLeadsCard;
